import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ContentCopy, CheckCircle, Security } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import { mfaAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AppHeader from './AppHeader';

const STEPS = ['Install authenticator app', 'Scan QR code', 'Enter verification code'];

const MFASetup = () => {
  const navigate = useNavigate();
  const { loadUser } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [alreadyEnabled, setAlreadyEnabled] = useState(false);

  const [deviceId, setDeviceId] = useState(null);
  const [provisioningUrl, setProvisioningUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [copied, setCopied] = useState(false);

  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const codeRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await mfaAPI.setup();
        const d = res.data;
        if (d.mfa_enabled && !d.provisioning_url) {
          setAlreadyEnabled(true);
        } else {
          setDeviceId(d.device_id);
          setProvisioningUrl(d.provisioning_url);
          setSecret(d.secret || '');
        }
      } catch (err) {
        setError('Failed to initialize MFA setup.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatSecret = (s) => {
    return s.replace(/(.{4})/g, '$1 ').trim();
  };

  const handleVerify = async () => {
    const trimmed = code.replace(/\s/g, '');
    if (trimmed.length !== 6 || !/^\d{6}$/.test(trimmed)) {
      setError('Please enter a 6-digit code.');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      await mfaAPI.verify(trimmed, deviceId);
      setSuccess(true);
      localStorage.setItem('mfa_verified', 'true');
      await loadUser();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleVerify();
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable MFA? This will remove your authenticator device.')) return;
    try {
      await mfaAPI.disable();
      await loadUser();
      setAlreadyEnabled(false);
      navigate('/');
    } catch (err) {
      setError('Failed to disable MFA.');
    }
  };

  if (loading) {
    return (
      <Box>
        <AppHeader backTo="/" />
        <Container maxWidth="sm" sx={{ mt: 6, textAlign: 'center' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  if (success) {
    return (
      <Box>
        <AppHeader backTo="/" />
        <Container maxWidth="sm" sx={{ mt: 6 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              MFA Enabled
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your authenticator app is now linked to your account. You will be prompted for a code each time you sign in.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/')}>
              Go to Dashboard
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (alreadyEnabled) {
    return (
      <Box>
        <AppHeader backTo="/" />
        <Container maxWidth="sm" sx={{ mt: 6 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Security color="primary" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              MFA is Enabled
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your account is protected with two-factor authentication.
            </Typography>
            <Button variant="outlined" color="error" onClick={handleDisable}>
              Disable MFA
            </Button>
            <Box sx={{ mt: 2 }}>
              <Button variant="text" onClick={() => navigate('/')}>
                Back to Dashboard
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <AppHeader backTo="/" />
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security color="primary" />
            Set Up Two-Factor Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add an extra layer of security to your account. You will need an authenticator app like Google Authenticator, Microsoft Authenticator, or Authy.
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Install one of these authenticator apps on your phone:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 3 }}>
                <li><Typography variant="body2">Google Authenticator (iOS / Android)</Typography></li>
                <li><Typography variant="body2">Microsoft Authenticator (iOS / Android)</Typography></li>
                <li><Typography variant="body2">Authy (iOS / Android / Desktop)</Typography></li>
              </Box>
              <Button variant="contained" onClick={() => setActiveStep(1)}>
                I have an authenticator app
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Open your authenticator app and scan this QR code:
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3, p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                {provisioningUrl && (
                  <QRCodeSVG value={provisioningUrl} size={200} level="M" />
                )}
              </Box>

              <Divider sx={{ my: 2 }}>or enter the key manually</Divider>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: 2, fontWeight: 600 }}>
                  {formatSecret(secret)}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton size="small" onClick={handleCopySecret}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                <Button variant="text" onClick={() => setActiveStep(0)}>
                  Back
                </Button>
                <Button variant="contained" onClick={() => { setActiveStep(2); setTimeout(() => codeRef.current?.focus(), 100); }}>
                  I have scanned the code
                </Button>
              </Box>
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Enter the 6-digit code shown in your authenticator app to confirm setup:
              </Typography>
              <TextField
                inputRef={codeRef}
                label="6-digit code"
                value={code}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(v);
                }}
                onKeyDown={handleKeyDown}
                fullWidth
                autoFocus
                inputProps={{
                  maxLength: 6,
                  style: { fontSize: '1.5rem', letterSpacing: 8, textAlign: 'center', fontFamily: 'monospace' },
                  inputMode: 'numeric',
                  autoComplete: 'one-time-code',
                }}
                sx={{ mb: 3 }}
              />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                <Button variant="text" onClick={() => setActiveStep(1)}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleVerify}
                  disabled={verifying || code.length !== 6}
                >
                  {verifying ? 'Verifying...' : 'Verify & Enable MFA'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default MFASetup;
